import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import Seo from "../components/Seo";

export default function MemberLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/portal";
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const inputCls = "w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus-visible:ring-2 focus-visible:ring-electric/40 transition";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const u = await login(form.email, form.password);
      toast.success(`Welcome, ${u.name.split(" ")[0]}`);
      navigate(u.role === "admin" ? "/admin" : next);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main id="main-content" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5 py-20 relative overflow-hidden" data-testid="login-page">
      <Seo title="Sign In" path="/login" />
      <div className="absolute inset-0 lightning-grid opacity-30" />
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-electric/15 rounded-full blur-3xl" />

      <Link to="/" className="absolute top-6 left-6 text-zinc-400 hover:text-electric flex items-center gap-2 text-sm" data-testid="login-back">
        <ArrowLeft className="w-4 h-4" /> Back to site
      </Link>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png" alt="The Push Center" className="w-20 h-20 rounded-full ring-2 ring-electric/40 mb-5" />
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-2">Welcome back</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Sign in.</h1>
        </div>

        <form onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8 space-y-5" data-testid="login-form">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">Email</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="login-email" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">Password</label>
            <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="login-password" />
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="login-error">{error}</div>
          )}

          <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-electric text-black px-7 py-4 rounded-lg font-bold tracking-wide hover:bg-[#4ade80] transition disabled:opacity-60" data-testid="login-submit">
            <LogIn className="w-4 h-4" />
            {submitting ? "Signing in..." : "Sign In"}
          </button>

          <p className="text-center text-sm text-zinc-400">
            New to The Push?{" "}
            <Link to="/signup" className="text-electric font-bold hover:underline" data-testid="login-to-signup">Create an account</Link>
          </p>
        </form>
      </motion.div>
    </main>
  );
}
